import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, ChevronLeft, ChevronRight, Calendar, Fuel, Gauge, Settings, 
  MapPin, Phone, Mail, Heart, Share2, Star, User, MessageCircle 
} from 'lucide-react';
import type { CarListing } from '../admin/types';

interface CarDetailModalProps {
  car: CarListing | null;
  isOpen: boolean;
  onClose: () => void;
  onInquire?: (car: CarListing) => void;
}

export default function CarDetailModal({ car, isOpen, onClose, onInquire }: CarDetailModalProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);

  if (!car) return null;

  const images = car.images && car.images.length > 0 ? car.images : (car.image_url ? [car.image_url] : []);
  const hasMultipleImages = images.length > 1;

  const formatPrice = (price: number) => {
    return `R ${(price / 100).toLocaleString()}`;
  };

  const formatMileage = (mileage?: number) => {
    if (!mileage) return 'N/A';
    return `${mileage.toLocaleString()} km`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'text-green-600 bg-green-100';
      case 'reserved': return 'text-yellow-600 bg-yellow-100';
      case 'under-offer': return 'text-orange-600 bg-orange-100';
      case 'sold': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const nextImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev + 1) % images.length);
    }
  };

  const prevImage = () => {
    if (hasMultipleImages) {
      setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    }
  };

  const handleImageClick = () => {
    if (images.length > 0) {
      setIsImageViewerOpen(true);
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${car.make} ${car.model} - ${car.year}`,
        text: `Check out this ${car.make} ${car.model} for ${formatPrice(car.price)}`,
        url: window.location.href,
      });
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      // You could show a toast here
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Main Modal */}
          <motion.div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          >
            <motion.div
              className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {car.make} {car.model}
                  </h2>
                  <p className="text-gray-600">{car.year} • {car.color}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleShare}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  >
                    <Share2 size={20} />
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
                {/* Image Section */}
                <div className="space-y-4">
                  {/* Main Image */}
                  <div className="relative">
                    {images.length > 0 ? (
                      <div className="relative h-64 md:h-80 bg-gray-200 rounded-lg overflow-hidden">
                        <img
                          src={images[currentImageIndex]}
                          alt={`${car.make} ${car.model}`}
                          className="w-full h-full object-cover cursor-pointer"
                          onClick={handleImageClick}
                          loading="eager"
                          decoding="async"
                        />
                        
                        {/* Status Badge */}
                        <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(car.status)}`}>
                          {car.status.replace('-', ' ').toUpperCase()}
                        </div>

                        {/* Featured Badge */}
                        {car.is_featured && (
                          <div className="absolute top-3 left-3 bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 px-3 py-1 rounded-full text-xs font-medium">
                            <Star size={12} className="inline mr-1" />
                            Featured
                          </div>
                        )}

                        {/* Navigation Arrows */}
                        {hasMultipleImages && (
                          <>
                            <button
                              onClick={prevImage}
                              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                            >
                              <ChevronLeft size={20} />
                            </button>
                            <button
                              onClick={nextImage}
                              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                            >
                              <ChevronRight size={20} />
                            </button>
                          </>
                        )}

                        {/* Image Counter */}
                        {hasMultipleImages && (
                          <div className="absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-3 py-1 rounded-full text-xs">
                            {currentImageIndex + 1} / {images.length}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="h-64 md:h-80 bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-gray-400">No Images Available</span>
                      </div>
                    )}

                    {/* Thumbnail Strip */}
                    {hasMultipleImages && (
                      <div className="flex gap-2 mt-3 overflow-x-auto">
                        {images.map((image, index) => (
                          <button
                            key={index}
                            onClick={() => setCurrentImageIndex(index)}
                            className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 ${
                              currentImageIndex === index ? 'border-blue-500' : 'border-gray-200'
                            }`}
                          >
                            <img
                              src={image}
                              alt={`Thumbnail ${index + 1}`}
                              className="w-full h-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Details Section */}
                <div className="space-y-6">
                  {/* Price */}
                  <div>
                    <h3 className="text-3xl font-bold text-red-600 mb-2">
                      {formatPrice(car.price)}
                    </h3>
                  </div>

                  {/* Specifications */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Gauge className="text-gray-600" size={20} />
                      <div>
                        <p className="text-sm text-gray-600">Mileage</p>
                        <p className="font-medium">{formatMileage(car.mileage)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Calendar className="text-gray-600" size={20} />
                      <div>
                        <p className="text-sm text-gray-600">Year</p>
                        <p className="font-medium">{car.year}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Fuel className="text-gray-600" size={20} />
                      <div>
                        <p className="text-sm text-gray-600">Fuel Type</p>
                        <p className="font-medium capitalize">{car.fuel_type || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <Settings className="text-gray-600" size={20} />
                      <div>
                        <p className="text-sm text-gray-600">Transmission</p>
                        <p className="font-medium capitalize">{car.transmission || 'N/A'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Additional Details */}
                  {(car.engine_size || car.condition) && (
                    <div className="grid grid-cols-2 gap-4">
                      {car.engine_size && (
                        <div>
                          <p className="text-sm text-gray-600">Engine Size</p>
                          <p className="font-medium">{car.engine_size}</p>
                        </div>
                      )}
                      {car.condition && (
                        <div>
                          <p className="text-sm text-gray-600">Condition</p>
                          <p className="font-medium capitalize">{car.condition.replace('-', ' ')}</p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Location */}
                  {car.location && (
                    <div className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                      <MapPin className="text-gray-600" size={20} />
                      <div>
                        <p className="text-sm text-gray-600">Location</p>
                        <p className="font-medium">{car.location}</p>
                      </div>
                    </div>
                  )}

                  {/* Contact Information */}
                  {(car.contact_name || car.contact_phone || car.contact_email) && (
                    <div className="space-y-3">
                      <h4 className="font-semibold text-gray-900">Contact Information</h4>
                      {car.contact_name && (
                        <div className="flex items-center gap-3">
                          <User className="text-gray-600" size={16} />
                          <span className="text-gray-700">{car.contact_name}</span>
                        </div>
                      )}
                      {car.contact_phone && (
                        <div className="flex items-center gap-3">
                          <Phone className="text-gray-600" size={16} />
                          <a href={`tel:${car.contact_phone}`} className="text-blue-600 hover:underline">
                            {car.contact_phone}
                          </a>
                        </div>
                      )}
                      {car.contact_email && (
                        <div className="flex items-center gap-3">
                          <Mail className="text-gray-600" size={16} />
                          <a href={`mailto:${car.contact_email}`} className="text-blue-600 hover:underline">
                            {car.contact_email}
                          </a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Description */}
                  {car.description && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Description</h4>
                      <p className="text-gray-700 leading-relaxed">{car.description}</p>
                    </div>
                  )}

                  {/* Features */}
                  {car.features && car.features.length > 0 && (
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">Features</h4>
                      <div className="grid grid-cols-2 gap-2">
                        {car.features.map((feature, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-sm capitalize">{feature.replace('-', ' ')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="space-y-3 pt-4">
                    <button
                      onClick={() => onInquire?.(car)}
                      className="w-full bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-2"
                    >
                      <MessageCircle size={20} />
                      Inquire About This Vehicle
                    </button>
                    
                    <div className="grid grid-cols-1 gap-2">
                      {car.contact_phone && (
                        <a
                          href={`tel:${car.contact_phone}`}
                          className="w-full bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2"
                        >
                          <Phone size={20} />
                          Call {car.contact_name || 'Us'} Now
                        </a>
                      )}
                      {car.contact_email && (
                        <a
                          href={`mailto:${car.contact_email}?subject=Inquiry about ${car.make} ${car.model} (${car.year})`}
                          className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-2"
                        >
                          <Mail size={20} />
                          Email {car.contact_name || 'Us'}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Full Screen Image Viewer */}
          {isImageViewerOpen && images.length > 0 && (
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-95 flex items-center justify-center z-60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsImageViewerOpen(false)}
            >
              <div className="relative max-w-4xl max-h-full">
                <img
                  src={images[currentImageIndex]}
                  alt={`${car.make} ${car.model}`}
                  className="max-w-full max-h-full object-contain"
                />
                
                {/* Close Button */}
                <button
                  onClick={() => setIsImageViewerOpen(false)}
                  className="absolute top-4 right-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-75"
                >
                  <X size={24} />
                </button>

                {/* Navigation */}
                {hasMultipleImages && (
                  <>
                    <button
                      onClick={prevImage}
                      className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-75"
                    >
                      <ChevronLeft size={24} />
                    </button>
                    <button
                      onClick={nextImage}
                      className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black bg-opacity-50 text-white p-3 rounded-full hover:bg-opacity-75"
                    >
                      <ChevronRight size={24} />
                    </button>
                  </>
                )}

                {/* Image Counter */}
                {hasMultipleImages && (
                  <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-full">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
}
